<script setup lang="ts">
import { ref, computed } from 'vue';
import type { MenuItem } from 'primevue/menuitem';
import { TieredMenu } from 'primevue';
import type { DropdownMenuItem } from '../types/common';
import { NO_SUBHEADER_KEY, OTHER_GROUP_KEY, BLANK_TARGET } from 'vince-core/constants/common';

const props = defineProps({
    label: {
        type: String,
        default: ''
    },
    icon: {
        type: String,
        default: ''
    },
    options: {
        type: Array as PropType<DropdownMenuItem[]>,
        default: () => []
    },
    hideLabel: {
        type: Boolean,
        default: false
    },
    nestedOptions: {
        type: Array as PropType<MenuItem[]>,
        default: () => []
    }
});

const menu = ref();
const activeMenu = ref<boolean>(false);
const { t } = useI18n();

const menuItems = computed(() => {
    if (!props.options || !props.options.length) {
        return [];
    }
    let hasSubheader = false;
    for (const option of props.options as DropdownMenuItem[]) {
        if (option.subheader && option.subheader.trim()) {
            hasSubheader = true;
            break;
        }
    }
    if (!hasSubheader) {
        return (props.options as DropdownMenuItem[]).map(option => ({
            label: option.labelKey != null ? t(option.labelKey) : option.label,
            icon: option.icon,
            url: option.href,
            command: option.command
        }));
    }
    const groups: Record<string, DropdownMenuItem[]> = {};
    (props.options as DropdownMenuItem[]).forEach((option) => {
        const groupKey = option.subheader && option.subheader.trim() ? option.subheader : NO_SUBHEADER_KEY;
        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }
        groups[groupKey]!.push(option);
    });
    return Object.entries(groups).map(([label, items]) => ({
        label: label === NO_SUBHEADER_KEY ? undefined : label,
        items: items.map(option => ({
            label: option.labelKey != null ? t(option.labelKey) : option.label,
            icon: option.icon,
            url: option.href,
            command: option.command
        }))
    }));
});

const groupedNestedOptions = computed(() => {
    if (!props.nestedOptions || !props.nestedOptions.length) { return []; }
    return groupMenuBySubheader(props.nestedOptions);
});

function groupMenuBySubheader (rawMenu: MenuItem[]): MenuItem[] {
    return rawMenu.map((section) => {
        const grouped = (section.items as MenuItem[]).reduce((acc: Record<string, MenuItem[]>, item: MenuItem) => {
            const groupKey = (item as DropdownMenuItem).subheader || OTHER_GROUP_KEY;
            if (!acc[groupKey]) { acc[groupKey] = []; }
            (acc[groupKey] ?? []).push({ ...item, subheader: undefined });
            return acc;
        }, {} as Record<string, MenuItem[]>);

        const groupedItems: MenuItem[] = Object.entries(grouped).map(([subheader, items]) => ({
            label: subheader,
            items: items as MenuItem[]
        }));

        return {
            label: section.label,
            icon: section.icon,
            items: groupedItems
        } as MenuItem;
    });
}

const toggle = (event: Event) => {
    menu.value.toggle(event);
    activeMenu.value = !activeMenu.value;
};

const handleClickOutside = (event: MouseEvent) => {
    const menuElement = menu.value?.$el;
    if (menuElement && !menuElement.contains(event.target as Node)) {
        if (menu.value.focused) { return; }
        activeMenu.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

</script>

<template>
    <div
        class=" py-2 rounded flex items-center cursor-pointer menu-dropdown"
        :class="[!hideLabel ? 'px-4' : 'px-2', { 'active-menu': activeMenu, 'ml-4': !hideLabel }]"
        @click="toggle"
    >
        <slot name="icon">
            <i
                :class="icon"
                class="mr-2 text-s"
                :style="activeMenu && { color: 'var(--secondary-color' }"
            />
        </slot>
        <span
            v-if="!hideLabel"
            class="dropdown-label"
            :class="{ 'active-label': activeMenu }"
        >
            {{ label }}
        </span>
        <tiered-menu
            v-if="nestedOptions.length"
            ref="menu"
            :model="groupedNestedOptions"
            :popup="true"
        >
            <template #item="{ item, props }">
                <router-link v-if="item.url" v-slot="{ href, navigate }" :to="item.url" custom>
                    <a :href="href" v-bind="props.action" @click="navigate">
                        <span :class="item.icon" />
                        <span>{{ item.label }}</span>
                    </a>
                </router-link>
                <a v-else v-ripple :href="item.url" :target="item.target" v-bind="props.action">
                    <span :class="item.icon" />
                    <span>{{ item.label }}</span>
                </a>
            </template>
        </tiered-menu>
        <VLMenu
            v-else
            ref="menu"
            :model="menuItems"
            :popup="true"
        >
            <template #item="{ item, props }">
                <router-link v-if="item.url && !item.url.startsWith('http')" v-slot="{ href, navigate }" :to="item.url" custom>
                    <a :href="href" v-bind="props.action" @click="navigate">
                        <span :class="item.icon" />
                        <span>{{ item.label }}</span>
                    </a>
                </router-link>
                <a v-else v-ripple :href="item.url" :target="item.url && item.url.startsWith('http') ? BLANK_TARGET : item.target" v-bind="props.action">
                    <span :class="item.icon" />
                    <span>{{ item.label }}</span>
                </a>
            </template>
        </VLMenu>
        <i
            v-if="!hideLabel"
            class="pi pi-chevron-down ml-2 text-xs"
            :class="{ 'active-label': activeMenu }"
        />
    </div>
</template>

<style scoped>
.menu-dropdown:hover {
    color: var(--secondary-color-hover);
}
.active-menu {
    color: var(--secondary-color);
    background-color: var(--secondary-color-selected-dark);
}
.active-label {
    color: var(--primary-color);
    transition: color 0.2s ease;
}
</style>
